import React, { useState } from 'react';
import { academicYearOf } from './attendanceApi';
import useAttendance from './useAttendance';
import AttendanceGrid from './AttendanceGrid';
import HolidayDialog from './HolidayDialog';
import WhatsAppTemplateSender from './WhatsAppTemplateSender';
import BodyPortal from './BodyPortal';
import './AttendanceMaster.css';

// Master attendance screen.
//  - Class teacher: locked to user.class. Daily / Weekly / Monthly, any past month or academic year.
//  - Admin teacher / principal: class picker (all classes) + WhatsApp report bar.
// Two-click flow: [Save Attendance] -> [Send WhatsApp Report].
export default function AttendanceMaster({ user, onClose }) {
  const a = useAttendance(user);
  const [showHoliday, setShowHoliday] = useState(false);
  const [showWa, setShowWa] = useState(false);

  // Ask before throwing away unsaved edits.
  const ok = () => !a.dirty || window.confirm('Discard unsaved changes?');
  const clsLabel = (c) => (/^\d+$/.test(c) ? `Class ${c}` : c);
  const monthly = a.mode === 'monthly';

  return (
    <BodyPortal>
    <div className="am-root">
      <div className="am-header">
        <div className="am-header-text">
          <h2 className="am-title">📋 {a.isGlobal ? 'Master Attendance' : `Attendance – Class ${a.cls}`}</h2>
          <p className="am-sub">Academic year {academicYearOf(a.from)}{a.isGlobal && a.cls ? ` · ${clsLabel(a.cls)}` : ''}</p>
        </div>
        <button onClick={() => ok() && onClose()} aria-label="Close" className="am-close">✕</button>
      </div>

      <div className="am-body">
        <div className="am-card am-row">
          {a.isGlobal && (
            <div className="grow">
              <label className="am-label">Class</label>
              <select value={a.cls} className="am-select" onChange={(e) => { if (ok()) a.setCls(e.target.value); }}>
                {a.classes.map((c) => <option key={c} value={c}>{clsLabel(c)}</option>)}
              </select>
            </div>
          )}
          <div className="grow">
            <label className="am-label">View</label>
            <div className="am-seg">
              {['daily', 'weekly', 'monthly'].map((m) => (
                <button key={m} className={a.mode === m ? 'on' : ''} onClick={() => ok() && a.setMode(m)}>{m[0].toUpperCase() + m.slice(1)}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="am-card">
          <label className="am-label">{monthly ? 'Month' : 'Date'}</label>
          <div className="am-nav">
            <button className="am-nav-btn" aria-label="Previous" onClick={() => ok() && a.step(-1)}>◀</button>
            <input
              type={monthly ? 'month' : 'date'}
              value={monthly ? a.anchor.slice(0, 7) : a.anchor}
              max={a.today} className="am-date"
              onChange={(e) => { const v = e.target.value; if (v && ok()) a.setAnchor(monthly ? `${v}-01` : v); }}
            />
            <button className="am-nav-btn" aria-label="Next" disabled={a.to >= a.today} onClick={() => ok() && a.step(1)}>▶</button>
            <button className="am-nav-btn" onClick={() => ok() && a.setAnchor(a.today)}>Today</button>
          </div>
          <div className="am-range">Showing <b>{a.from === a.to ? a.from : `${a.from} → ${a.to}`}</b></div>
        </div>

        <div className="am-card">
          <div className="am-actions">
            <button onClick={() => a.markAll('present')} className="am-chip-btn am-green">✅ All Present</button>
            <button onClick={() => a.markAll('absent')} className="am-chip-btn am-red">❌ All Absent</button>
            <button onClick={() => setShowHoliday(true)} className="am-chip-btn am-amber">🏖 Holiday</button>
          </div>
          {a.mode === 'daily' && a.roster && (
            <div className="am-stats">
              <div className="am-stat am-s-p"><strong>{a.counts.present}</strong>Present</div>
              <div className="am-stat am-s-a"><strong>{a.counts.absent}</strong>Absent</div>
              <div className="am-stat am-s-l"><strong>{a.counts.leave}</strong>Leave</div>
              <div className="am-stat am-s-t"><strong>{a.counts.late}</strong>Late</div>
              <div className="am-stat am-s-n"><strong>{a.roster.students.length}</strong>Total</div>
            </div>
          )}
          <p className="am-hint">Tap a cell to change: P → A → L → T</p>
        </div>

        {a.loading && <p className="am-empty">Loading…</p>}
        {a.roster && !a.loading && (
          <AttendanceGrid students={a.roster.students} dates={a.dates} holidays={a.roster.holidays} draft={a.draft} onChange={a.change} />
        )}
      </div>

      <div className="am-bar">
        <div className="am-bar-in">
          {a.msg && <div className="am-msg">{a.msg}</div>}
          <div className="am-btns">
            <button onClick={a.save} disabled={!a.roster} className={`am-save ${a.dirty ? 'dirty' : ''}`}>
              {a.dirty ? '💾 Save (unsaved changes)' : '💾 Save Attendance'}
            </button>
            {a.isGlobal && a.saved && !a.dirty && (
              <button onClick={() => setShowWa(true)} className="am-wa">📲 WhatsApp Report</button>
            )}
          </div>
        </div>
      </div>

      {showWa && a.saved && (
        <WhatsAppTemplateSender key={`${a.cls}-${a.saved}`} user={user} className={a.cls} date={a.saved} onClose={() => setShowWa(false)} />
      )}

      {showHoliday && (
        <HolidayDialog user={user} className={a.cls} isGlobal={a.isGlobal}
          initialDate={a.anchor > a.today ? a.today : a.anchor}
          onClose={() => setShowHoliday(false)} onDone={() => { setShowHoliday(false); a.load(); }} />
      )}
    </div>
    </BodyPortal>
  );
}
